import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChildren,
  QueryList,
  ElementRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { AccordionDataService, AccordionFolder } from '../../services/accordion-data.service';
import {
  SharedUsersDialogComponent,
  SharedUsersDialogData
} from '../shared-users-dialog/shared-users-dialog.component';

/**
 * Home Folders Accordion Component with Pagination Support
 *
 * This component displays folders in an accordion format with expandable file lists.
 * It now includes pagination support to handle large numbers of files efficiently.
 *
 * Features:
 * - Paginated file display (default: 20 items per page, max: 50)
 * - Page size selection (10, 20, 30, 40, 50 items)
 * - Navigation controls (first, previous, next, last page)
 * - Responsive design for mobile devices
 * - File selection across all pages
 * - Maintains selection state when navigating between pages
 *
 * Usage:
 * The parent component should provide the filesLoaded input when files are loaded:
 * <app-home-folders-accordion
 *   [filesLoaded]="filesLoadedData"
 *   ... other inputs ...
 * >
 */

const DICOMDIR_FILE_NAME = 'DICOMDIR';

export interface HomeFolder {
  name: string;
  id: string;
  color?: string;
  children?: any[];
  hasNotifications?: boolean;
  count?: string;
  type?: string;
  expanded?: boolean;
  loading?: boolean;
  files?: any[];
  restricted_to?: any[];
}

export interface FolderFile {
  fileName: string;
  fileDesc?: string;
  lastModified?: string;
  uploadedDate?: string;
  fdate?: string;
  ftime?: string;
  type?: string;
  fileId: string;
  hasNotifications?: boolean;
  hasCommentAttachments?: boolean; // Whether this file has comment attachments
  sharedUsers?: any[];
  parentFolderName?: string;
  parentFolder?: string;
  color?: string;
  viewerurl?: string;
  ftype?: string;
  filePath?: string;
  storename?: string;
  style?: any; // CSS styles object for ngStyle binding
  folderId?: string; // ID of the folder containing this file
  predefined?: boolean; // Whether this file is predefined/system file
  permission_delete?: boolean;
  ownerID?: string;
  id?: string;    // Firestore document ID — required by copy and comment handlers
  notes?: any[];  // Comment/attachment notes — required by comment handler
}

// Pagination interface
export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

@Component({
    selector: 'app-home-folders-accordion',
    templateUrl: './home-folders-accordion.component.html',
    styleUrls: ['./home-folders-accordion.component.scss'],
    standalone: false
})
export class HomeFoldersAccordionComponent implements OnInit, OnDestroy, OnChanges {
  @Input() folders: AccordionFolder[] = [];
  @Input() userOwnerID: string = '';
  @Input() currentFolder: string = '';
  @Input() currentFolderName: string = '';
  @Input() permissions: any = {
    createfolder: false,
    deletefiles: false,
    editfiles: false,
    sharefileicon: false
  };
  @Input() realRole: string = '';
  @Input() UserRoles: any = {};
  @Input() loading: boolean = false;
  @Input() loadingMessage: string = '';
  @Input() discsFolderName: string = '';
  @Input() filesLoaded: { [folderId: string]: FolderFile[] } = {};
  @Input() searchResults: any[] = [];
  @Input() searchQuery: string = '';
  @Input() isSearchActive: boolean = false;
  @Input() casename: string = '';
  @Input() ownerID: string = '';

  @Output() folderClick = new EventEmitter<HomeFolder>();
  @Output() createFolder = new EventEmitter<void>();
  @Output() loadFolderContents = new EventEmitter<string>();
  @Output() refreshFolderContents = new EventEmitter<string>(); // New output for refresh
  @Output() fileClick = new EventEmitter<FolderFile>();
  @Output() editFile = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() deleteFile = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() copyFile = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() generateDisc = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() addCommentOrAttachment = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() sharedIconClick = new EventEmitter<FolderFile>();
  @Output() folderNameClick = new EventEmitter<FolderFile>();
  @Output() studyClick = new EventEmitter<FolderFile>();
  @Output() folderSelectionChange = new EventEmitter<{ folderId: string; selectedFiles: FolderFile[] }>();
  @Output() allFoldersClosed = new EventEmitter<void>();
  @Output() searchResultFileClick = new EventEmitter<any>();
  @Output() searchResultFileSelectionChange = new EventEmitter<any>();
  @Output() clearSearchResults = new EventEmitter<void>();
  @Output() deleteFolder = new EventEmitter<{ folder: AccordionFolder; event: Event }>();
  @Output() createSubfolder = new EventEmitter<{ folder: AccordionFolder; event: Event }>();

  accordionFolders: AccordionFolder[] = [];
  private subscription: Subscription = new Subscription();

  // New property to track hierarchical display
  // displayFolders: AccordionFolder[] = [];

  // ViewChildren for folder elements to enable scrolling
  @ViewChildren('folderItem') folderItems!: QueryList<ElementRef>;

  // File selection tracking for each folder
  private folderSelections: Map<string, Set<string>> = new Map();

  // Pagination properties
  private paginationStates: Map<string, PaginationState> = new Map();
  readonly defaultPageSize = 5;
  readonly maxPageSize = 50;

  // Subfolder expansion tracking
  private subfolderExpansionStates: Map<string, boolean> = new Map();

  // Make Math available in template
  Math = Math;

  constructor(
    private accordionDataService: AccordionDataService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Subscribe to folder data changes
    this.subscription.add(
      this.accordionDataService.folders$.subscribe(folders => {
        // Filter out any updates that would decrease INDEX folder count inappropriately
        // const filteredFolders = this.protectIndexFolderCount(folders);
        this.folders = this.protectIndexFolderCount(folders);
        // this.accordionFolders = filteredFolders;

        // Update display folders for hierarchical view
        this.folders = this.updateDisplayFolders(this.folders, this.userOwnerID);
        // this.accordionFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);
        // this.displayFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);

        // Initialize pagination for new folders
        this.initializePaginationForFolders(this.protectIndexFolderCount(folders));

        // Update pagination state for folders that have files
        this.protectIndexFolderCount(folders).forEach(folder => {
          if (folder.files && folder.files.length > 0) {
            this.updatePaginationState(folder.id, folder.files.length);
          }
        });
      })
    );

    // Initialize with input folders (only if we have folders and no existing ones)
    if (this.folders.length > 0) {
      this.initializeInitialFolders();
    }
  }

  // Removed empty ngAfterViewInit lifecycle method to comply with linter rules
  private initializeInitialFolders(): void {
    if (this.folders.length === 0) return;

    const accordionFolders = this.folders.map(folder => ({
      id: folder.id,
      name: folder.name,
      color: folder.color,
      hasNotifications: folder.hasNotifications,
      count: folder.count,
      type: folder.type || 'folder',
      restrictedTo: folder.restricted_to,
      expanded: false,
      loading: false,
      refreshing: false,
      files: null
    }));

    this.accordionDataService.setFolders(accordionFolders);
    this.initializePaginationForFolders(accordionFolders);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Watch for changes to search results
    if (changes['isSearchActive'] || changes['searchResults']) {
      this.updateSearchResultsFolder();
    }

    // Watch for changes to the folders input property
    if (changes['folders'] && changes['folders'].currentValue) {
      const currentFolders = this.accordionDataService.getCurrentFolders();

      // Only re-initialize if we have no existing folders or if the folder IDs have changed
      const currentFolderIds = new Set(currentFolders.map(f => f.id));
      const newFolderIds = new Set(changes['folders'].currentValue.map((f: any) => f.id as string));

      // Check if the folder sets are different
      const hasChanges =
        currentFolderIds.size !== newFolderIds.size ||
        [...currentFolderIds].some(id => !newFolderIds.has(id)) ||
        [...newFolderIds].some((id: string) => !currentFolderIds.has(id));

      if (hasChanges) {
        this.initializeAccordionFolders(currentFolders);
      } else {
        // Folder structure is the same, just update the data without re-initializing
        this.updateFolderDataOnly(changes['folders'].currentValue);
      }

      // Always update display folders when folders input changes
      this.folders = this.updateDisplayFolders(this.folders, this.userOwnerID);
      // this.accordionFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);
      // this.displayFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Ensures the INDEX folder count is not overwritten by outdated or lower values.
   * If the current (previous) INDEX folder was updated within the past 30s,
   * and the new folder's count is missing or lower, retain the previous value.
   *
   * @param folders - Array of folders to process
   * @returns Updated folders array with protected INDEX count if applicable
   */
  private protectIndexFolderCount(folders: AccordionFolder[]): AccordionFolder[] {
    // Find the previous INDEX folder ("all") and its index in the new list
    const prevIndexFolder = this.accordionFolders.find(f => f.id === 'all' && f.name === 'INDEX');
    const indexInNew = folders.findIndex(f => f.id === 'all' && f.name === 'INDEX');

    // If there was a previous INDEX folder, it was updated recently,
    // and it still exists in the new folders array
    if (
      prevIndexFolder &&
      typeof prevIndexFolder.lastUpdated === 'number' &&
      Date.now() - prevIndexFolder.lastUpdated < 30000 &&
      indexInNew >= 0
    ) {
      const newIndexFolder = folders[indexInNew];
      const prevCount = Number(prevIndexFolder.count) || 0;
      const newCount = Number(newIndexFolder.count) || 0;
      // If the incoming INDEX folder is missing a timestamp or has a lower count, keep the recent one
      if (!newIndexFolder.lastUpdated || newCount < prevCount) {
        const updatedFolders = [...folders];
        updatedFolders[indexInNew] = { ...prevIndexFolder };
        return updatedFolders;
      }
    }
    // Otherwise, return folders unchanged
    return folders;
  }

  private updateFolderDataOnly(newFolders: any[]): void {
    const currentFolders = this.accordionDataService.getCurrentFolders();

    newFolders.forEach(newFolder => {
      const existingFolder = currentFolders.find(f => f.id === newFolder.id);
      if (existingFolder) {
        const isRecentIndex =
          existingFolder.id === 'all' &&
          existingFolder.name === 'INDEX' &&
          existingFolder.lastUpdated &&
          Date.now() - existingFolder.lastUpdated < 15000;

        this.accordionDataService.updateFolder({
          ...existingFolder,
          ...newFolder,
          count: isRecentIndex ? existingFolder.count : newFolder.count,
          type: newFolder.type || existingFolder.type
        });
      }
    });

    if (this.realRole === this.UserRoles.consultant) {
      this.accordionDataService.setFolders(this.accordionDataService.getCurrentFolders());
    }
  }

  private initializeAccordionFolders(currentFolders): void {
    const existingFolderIds = new Set(currentFolders.map(f => f.id));
    const newFolders: AccordionFolder[] = [];

    for (const folder of this.folders) {
      if (!existingFolderIds.has(folder.id)) {
        newFolders.push({
          ...folder,
          type: folder.type || 'folder',
          expanded: false,
          loading: false,
          refreshing: false,
          files: null,
          level: 0,
          children: []
        });

        if (folder.children && Array.isArray(folder.children)) {
          for (const child of folder.children) {
            if (!existingFolderIds.has(child.id)) {
              newFolders.push({
                ...child,
                type: child.type || 'folder',
                expanded: false,
                loading: false,
                refreshing: false,
                files: null,
                parentId: folder.id,
                level: 1,
                children: []
              });
            }
          }
        }
      }
    }

    if (newFolders.length > 0) {
      const allFolders = [...currentFolders, ...newFolders];
      this.accordionDataService.setFolders(allFolders);
      this.initializePaginationForFolders(newFolders);
    }
  }

  // Pagination methods
  private initializePaginationForFolders(folders: AccordionFolder[]): void {
    folders.forEach(folder => {
      if (!this.paginationStates.has(folder.id)) {
        this.paginationStates.set(folder.id, {
          currentPage: 1,
          pageSize: this.defaultPageSize,
          totalItems: 0,
          totalPages: 0
        });
      }
    });
  }

  private updatePaginationState(folderId: string, totalItems: number): void {
    const state = this.paginationStates.get(folderId) || {
      currentPage: 1,
      pageSize: this.defaultPageSize,
      totalItems: 0,
      totalPages: 0
    };

    state.totalItems = totalItems;
    state.totalPages = Math.ceil(totalItems / state.pageSize);

    // Ensure current page is within bounds
    if (state.currentPage > state.totalPages && state.totalPages > 0) {
      state.currentPage = state.totalPages;
    }

    this.paginationStates.set(folderId, state);
  }

  getPaginationState(folderId: string): PaginationState {
    return (
      this.paginationStates.get(folderId) || {
        currentPage: 1,
        pageSize: this.defaultPageSize,
        totalItems: 0,
        totalPages: 0
      }
    );
  }

  getPaginatedFiles(folder: AccordionFolder): FolderFile[] {
    if (!folder.files || folder.files.length === 0) {
      return [];
    }

    const state = this.getPaginationState(folder.id);
    const startIndex = (state.currentPage - 1) * state.pageSize;
    const endIndex = startIndex + state.pageSize;
    const paginatedFiles = folder.files.slice(startIndex, endIndex);
    paginatedFiles.forEach(file => {
      file.permission_delete = this.checkOwnership(file);
    });
    return paginatedFiles;
  }

  onPageChange(folderId: string, page: number): void {
    const state = this.paginationStates.get(folderId);
    if (state && page >= 1 && page <= state.totalPages) {
      state.currentPage = page;
      this.paginationStates.set(folderId, state);
    }
  }

  onPageSizeChange(folderId: string, pageSize: number): void {
    const state = this.paginationStates.get(folderId);
    if (state) {
      state.pageSize = Math.min(Math.max(pageSize, 10), this.maxPageSize);
      state.currentPage = 1; // Reset to first page
      state.totalPages = Math.ceil(state.totalItems / state.pageSize);
      this.paginationStates.set(folderId, state);
    }
  }

  getPageSizeOptions(): number[] {
    return [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
  }

  onFolderClick(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    // Check if this folder has children - if so, toggle children visibility
    if (this.hasChildren(folder)) {
      this.toggleChildrenVisibility(folder);
      // Don't return here - allow the folder to also load its files
    }

    // Consultant and facilitator folders are no longer supported

    // Check if we're about to expand (before toggling)
    const willExpand = !folder.expanded;

    // Toggle expansion state
    this.accordionDataService.toggleFolderExpansion(folder.id);

    // Update display folders to reflect the new expansion state
    this.folders = this.updateDisplayFolders(this.folders, this.userOwnerID);
    // this.accordionFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);
    // this.displayFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);

    // Only emit folder click event when opening a folder (not when closing)
    if (willExpand) {
      // Emit folder click event to parent component to update currentFolderName
      this.folderClick.emit({
        name: folder.name,
        id: folder.id,
        color: folder.color,
        hasNotifications: folder.hasNotifications,
        count: folder.count,
        type: folder.type,
        expanded: willExpand,
        loading: folder.loading,
        files: folder.files
      });

      // If expanding and no files loaded yet, load from cache or backend.
      // Use === null (not !folder.files) so an empty array [] doesn't
      // permanently block reload — ![] is false, which would leave the
      // folder stuck on "No files" after a permission-denied result.
      if (folder.files === null && !folder.loading) {
        // First try to get files from cache
        const cachedFiles = this.accordionDataService.getCachedFiles(folder.id);

        if (cachedFiles) {
          // Use cached files
          this.accordionDataService.updateFolderFiles(folder.id, cachedFiles);
          // Update pagination state for cached files
          this.updatePaginationState(folder.id, cachedFiles.length);
        } else {
          // Load from backend if not in cache
          this.accordionDataService.setFolderLoading(folder.id, true);
          this.loadFolderContents.emit(folder.id);
        }
      }
    } else {
      // When closing a folder, check if all folders are now closed and emit event if needed
      // Add a delay to ensure the state has been fully updated
      setTimeout(() => {
        this.checkAndEmitAllFoldersClosed();
      }, 100);
    }

    // Scroll to the expanded folder if it was just opened
    if (willExpand) {
      // setTimeout(() => {
      //   this.scrollToFolder(folder.id);
      // }, 100); // Small delay to ensure the DOM has updated
    }
  }

  /**
   * Scrolls to the specified folder to make it visible at the top
   * @param folderId The ID of the folder to scroll to
   */
  private scrollToFolder(folderId: string): void {
    if (!this.folderItems) {
      return;
    }

    // Find the folder element by its index in the accordionFolders array
    const folderIndex = this.folders.findIndex(f => f.id === folderId);
    // const folderIndex = this.accordionFolders.findIndex(f => f.id === folderId);
    if (folderIndex === -1) {
      return;
    }

    // Get the folder element
    const folderElements = this.folderItems.toArray();
    if (folderIndex >= folderElements.length) {
      return;
    }

    const folderElement = folderElements[folderIndex];
    if (folderElement && folderElement.nativeElement) {
      // Scroll the folder to the top with smooth behavior
      folderElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }

  onRefreshFolder(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    // Set refreshing state
    this.accordionDataService.setFolderRefreshing(folder.id, true);

    // Update display folders to reflect the refreshing state
    this.folders = this.updateDisplayFolders(this.folders, this.userOwnerID);
    // this.accordionFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);
    // this.displayFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);

    // Emit refresh event to parent component
    this.refreshFolderContents.emit(folder.id);
  }

  onCreateFolder(): void {
    this.createFolder.emit();
  }

  getFolderIcon(folder: AccordionFolder): string {
    if (folder.type === 'search-results') {
      return 'search';
    }
    if (false) {
      // Consultant folders are no longer supported
      return 'person';
    }
    if (false) {
      // Facilitator folders are no longer supported
      return 'business';
    }
    if (folder.hasNotifications) {
      return 'notifications';
    }
    return 'folder';
  }

  getFolderColor(folder: AccordionFolder): string {
    return folder.color || '#757575';
  }

  /**
   * Get the display name for a folder, including email address for individual folders
   * @param folder The folder to get display name for
   * @returns The display name with email if applicable
   */
  getFolderDisplayName(folder: AccordionFolder): string {
    // Consultant and facilitator folders are no longer supported

    // For individual user folders (those with email in the name pattern), show as is
    if (folder.name && folder.name.includes('@') && folder.name.includes(' - ')) {
      return folder.name; // Already contains email in format "Name - email@domain.com"
    }

    // For regular folders, just return the name
    return folder.name;
  }

  isExpanded(folder: AccordionFolder): boolean {
    // First check if the folder has an expanded property (from displayFolders)
    if (folder.expanded !== undefined) {
      return folder.expanded;
    }

    // Fallback: Get the current state from the accordion data service
    const currentFolders = this.accordionDataService.getCurrentFolders();
    const currentFolder = currentFolders.find(f => f.id === folder.id);
    const result = currentFolder ? currentFolder.expanded || false : false;
    return result;
  }

  areAllFoldersClosed(): boolean {
    // Get the current state from the accordion data service
    const currentFolders = this.accordionDataService.getCurrentFolders();
    return currentFolders.every(folder => !folder.expanded);
  }

  private checkAndEmitAllFoldersClosed(): void {
    if (this.areAllFoldersClosed()) {
      this.allFoldersClosed.emit();
    }
  }

  isRefreshing(folder: AccordionFolder): boolean {
    return this.accordionDataService.isFolderRefreshing(folder.id);
  }

  onFileClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    // Emit file click event to parent component
    this.fileClick.emit(file);
  }

  onFolderMouseDown(folder: AccordionFolder, event: Event): void {
    // Handle mouse down if needed
  }

  // Method to handle files loaded from backend
  onFilesLoaded(folderId: string, files: FolderFile[]): void {
    // Update pagination state when files are loaded
    this.updatePaginationState(folderId, files.length);

    // Update display folders to reflect the loaded files
    this.folders = this.updateDisplayFolders(this.folders, this.userOwnerID);
    // this.accordionFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);
    // this.displayFolders = this.updateDisplayFolders(this.folders, this.userOwnerID);
  }

  // File selection methods
  isFileSelected(folder: AccordionFolder, file: FolderFile): boolean {
    const selection = this.folderSelections.get(folder.id);
    return selection ? selection.has(file.fileId) : false;
  }

  toggleFileSelection(folder: AccordionFolder, file: FolderFile, event: MatCheckboxChange): void {
    let selection = this.folderSelections.get(folder.id);
    if (!selection) {
      selection = new Set();
      this.folderSelections.set(folder.id, selection);
    }

    const isCurrentlySelected = selection.has(file.fileId);

    if (isCurrentlySelected) {
      selection.delete(file.fileId);
    } else {
      selection.add(file.fileId);
    }

    // Synchronize selection with INDEX folder (if not already in INDEX)
    if (folder.id !== 'all') {
      this.synchronizeWithIndexFolder(file.fileId, !isCurrentlySelected);
    } else {
      // If we're in INDEX folder, synchronize with the specific folder this file belongs to
      this.synchronizeWithSpecificFolder(file);
    }

    // Emit selection change
    this.folderSelectionChange.emit({
      folderId: folder.id,
      selectedFiles: this.getSelectedFiles(folder.id)
    });
  }

  isAllSelected(folder: AccordionFolder): boolean {
    if (!folder.files || folder.files.length === 0) return false;
    const selection = this.folderSelections.get(folder.id);
    return selection ? selection.size === folder.files.length : false;
  }

  isPartiallySelected(folder: AccordionFolder): boolean {
    if (!folder.files || folder.files.length === 0) return false;
    const selection = this.folderSelections.get(folder.id);
    if (!selection) return false;
    return selection.size > 0 && selection.size < folder.files.length;
  }

  masterToggle(folder: AccordionFolder, event: MatCheckboxChange): void {
    // MatCheckboxChange doesn't have stopPropagation, so we don't need to call it

    if (!folder.files || folder.files.length === 0) return;

    let selection = this.folderSelections.get(folder.id);
    if (!selection) {
      selection = new Set();
      this.folderSelections.set(folder.id, selection);
    }

    // Use the event's checked property to determine the action
    const shouldSelectAll = event.checked;

    if (shouldSelectAll) {
      // Select all
      folder.files.forEach(file => {
        selection.add(file.fileId);

        // Synchronize with INDEX folder if not in INDEX
        if (folder.id !== 'all') {
          this.synchronizeWithIndexFolder(file.fileId, true);
        }
      });

      // If we're in INDEX, synchronize with all specific folders
      if (folder.id === 'all') {
        this.synchronizeIndexWithSpecificFolders();
      }
    } else {
      // Deselect all
      selection.clear();

      // Synchronize with INDEX folder if not in INDEX
      if (folder.id !== 'all') {
        folder.files.forEach(file => {
          this.synchronizeWithIndexFolder(file.fileId, false);
        });
      } else {
        // If we're in INDEX, synchronize with all specific folders
        this.synchronizeIndexWithSpecificFolders();
      }
    }

    // Emit selection change
    this.folderSelectionChange.emit({
      folderId: folder.id,
      selectedFiles: this.getSelectedFiles(folder.id)
    });
  }

  onEditFile(file: FolderFile, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.editFile.emit({ file, event });
  }

  onDeleteFile(file: FolderFile, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.deleteFile.emit({ file, event });
  }

  onCopyFile(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.copyFile.emit({ file, event });
  }

  onGenerateDisc(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.generateDisc.emit({ file, event });
  }

  onAddCommentOrAttachment(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.addCommentOrAttachment.emit({ file, event });
  }

  onSharedIconClick(file: FolderFile, event: Event): void {
    event.stopPropagation();

    // Show the shared users dialog
    const dialogData: SharedUsersDialogData = {
      fileName: file.fileName,
      fileId: file.fileId,
      casename: this.casename,
      sharedUsers: file.sharedUsers || []
    };

    const dialogRef = this.dialog.open(SharedUsersDialogComponent, {
      data: dialogData,
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '85vh',
      disableClose: false,
      panelClass: 'shared-users-dialog-panel'
    });

    // Handle dialog result to update the file's shared users data
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'unshared') {
        // Update the file's sharedUsers array with the remaining users
        file.sharedUsers = result.remainingUsers;

        // Create a new file object with updated sharedUsers to ensure the event has the latest data
        const updatedFile = {
          ...file,
          sharedUsers: result.remainingUsers
        };

        // Emit the updated file for backward compatibility
        this.sharedIconClick.emit(updatedFile);
      } else if (result && result.action === 'closed') {
        // Dialog was closed normally, emit the current file
        this.sharedIconClick.emit(file);
      }
    });

    // Also emit the event for backward compatibility
    this.sharedIconClick.emit(file);
  }

  onFolderNameClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.folderNameClick.emit(file);
  }

  isDicomFile(file: FolderFile): boolean {
    return file.fileName === DICOMDIR_FILE_NAME || file.type === 'dicom';
  }

  isInactiveStore(file: FolderFile): boolean {
    return file.storename === 'inactive' || file.storename === 'Inactive';
  }

  onStudyClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.studyClick.emit(file);
  }

  getSelectedFiles(folderId: string): FolderFile[] {
    const selection = this.folderSelections.get(folderId);
    if (!selection || selection.size === 0) return [];

    // const folder = this.accordionFolders.find(f => f.id === folderId);
    const folder = this.folders.find(f => f.id === folderId);
    if (!folder || !folder.files) return [];

    return folder.files.filter(file => selection.has(file.fileId));
  }

  clearSelection(folderId: string): void {
    this.folderSelections.delete(folderId);
  }

  clearAllSelections(): void {
    this.folderSelections.clear();
  }

  /**
   * Synchronize file selection with INDEX folder
   */
  private synchronizeWithIndexFolder(fileId: string, isSelected: boolean): void {
    let indexSelection = this.folderSelections.get('all');
    if (!indexSelection) {
      indexSelection = new Set();
      this.folderSelections.set('all', indexSelection);
    }

    if (isSelected) {
      indexSelection.add(fileId);
    } else {
      indexSelection.delete(fileId);
    }
  }

  /**
   * Synchronize file selection with the specific folder this file belongs to
   */
  private synchronizeWithSpecificFolder(file: FolderFile): void {
    // Find the specific folder this file belongs to based on parentFolderName or parentFolder
    const targetFolderId = this.findFolderIdByFile(file);
    if (targetFolderId && targetFolderId !== 'all') {
      let targetSelection = this.folderSelections.get(targetFolderId);
      if (!targetSelection) {
        targetSelection = new Set();
        this.folderSelections.set(targetFolderId, targetSelection);
      }

      // Check if file is selected in INDEX
      const indexSelection = this.folderSelections.get('all');
      const isSelectedInIndex = indexSelection ? indexSelection.has(file.fileId) : false;

      if (isSelectedInIndex) {
        targetSelection.add(file.fileId);
      } else {
        targetSelection.delete(file.fileId);
      }
    }
  }

  /**
   * Find the folder ID that contains this file based on parent folder information
   */
  private findFolderIdByFile(file: FolderFile): string | null {
    const parentFolderName = file.parentFolderName || file.parentFolder;
    if (!parentFolderName) return null;

    // Find the folder that matches the parent folder name
    // const targetFolder = this.accordionFolders.find(folder => folder.name === parentFolderName && folder.id !== 'all');
    const targetFolder = this.folders.find(folder => folder.name === parentFolderName && folder.id !== 'all');

    return targetFolder ? targetFolder.id : null;
  }

  /**
   * Synchronize INDEX folder selections with specific folders
   */
  private synchronizeIndexWithSpecificFolders(): void {
    const indexSelection = this.folderSelections.get('all');
    if (!indexSelection) return;

    // Group files by their parent folder
    const filesByFolder = new Map<string, string[]>();

    // Find the INDEX folder
    // const indexFolder = this.accordionFolders.find(f => f.id === 'all');
    const indexFolder = this.folders.find(f => f.id === 'all');
    if (!indexFolder || !indexFolder.files) return;

    indexFolder.files.forEach(file => {
      const folderId = this.findFolderIdByFile(file);
      if (folderId) {
        if (!filesByFolder.has(folderId)) {
          filesByFolder.set(folderId, []);
        }
        filesByFolder.get(folderId)!.push(file.fileId);
      }
    });

    // Update each specific folder's selection
    filesByFolder.forEach((fileIds, folderId) => {
      let folderSelection = this.folderSelections.get(folderId);
      if (!folderSelection) {
        folderSelection = new Set();
        this.folderSelections.set(folderId, folderSelection);
      }

      fileIds.forEach(fileId => {
        if (indexSelection.has(fileId)) {
          folderSelection.add(fileId);
        } else {
          folderSelection.delete(fileId);
        }
      });
    });
  }

  trackByFileId(index: number, file: FolderFile): string {
    return file.fileId || index.toString();
  }

  trackByFolderId(index: number, folder: AccordionFolder): string {
    return folder.id || index.toString();
  }

  /**
   * Format date string to a readable format
   * @param dateString - The date string to format
   * @returns Formatted date string or empty string if invalid
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';

    try {
      // Handle various date formats
      let date: Date;

      // Check if it's already a formatted date string
      if (dateString.includes('GMT') || dateString.includes('UTC')) {
        date = new Date(dateString);
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
        // ISO format: YYYY-MM-DD
        date = new Date(dateString);
      } else if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
        // US format: MM/DD/YYYY
        date = new Date(dateString);
      } else {
        // Try parsing as is
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return '';
    }
  }

  /**
   * Format date and time string to a readable format
   * @param dateString - The date string to format
   * @returns Formatted date and time string or empty string if invalid
   */
  formatDateTime(dateString: string): string {
    if (!dateString) return '';

    try {
      // Handle various date formats
      let date: Date;

      // Check if it's already a formatted date string
      if (dateString.includes('GMT') || dateString.includes('UTC')) {
        date = new Date(dateString);
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
        // ISO format: YYYY-MM-DD
        date = new Date(dateString);
      } else if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
        // US format: MM/DD/YYYY
        date = new Date(dateString);
      } else {
        // Try parsing as is
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  /**
   * Format file date (fdate) and time (ftime) combination
   * @param fdate - The file date
   * @param ftime - The file time
   * @returns Formatted date and time string or empty string if invalid
   */
  formatFileDateTime(fdate: string, ftime: string): string {
    if (!fdate) return '';

    try {
      // If we have both date and time, combine them
      if (ftime) {
        const dateTimeString = `${fdate} ${ftime}`;
        const date = new Date(dateTimeString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      // If only date is available
      const date = new Date(fdate);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }

      return '';
    } catch (error) {
      return '';
    }
  }

  // Search Results Methods
  onSearchResultFileClick(file: any): void {
    this.searchResultFileClick.emit(file);
  }

  onSearchResultFileSelectionChange(file: any): void {
    this.searchResultFileSelectionChange.emit(file);
  }

  onClearSearchResults(): void {
    this.clearSearchResults.emit();
  }

  // Method to create search results folder
  createSearchResultsFolder(): AccordionFolder {
    return {
      id: 'search-results',
      name: 'Search Results',
      color: '#667eea',
      hasNotifications: false,
      count: this.searchResults && this.searchResults.length > 0 ? this.searchResults.length.toString() : '0',
      type: 'search-results',
      expanded: true,
      loading: false,
      refreshing: false,
      files: this.searchResults || []
    };
  }

  // Method to update search results folder
  private updateSearchResultsFolder(): void {
    const currentFolders = this.accordionDataService.getCurrentFolders();
    let updatedFolders = [...currentFolders];

    // Remove existing search results folder if it exists
    updatedFolders = updatedFolders.filter(folder => folder.id !== 'search-results');

    if (this.isSearchActive) {
      // Add search results folder at the beginning (even if no results)
      const searchResultsFolder = this.createSearchResultsFolder();
      updatedFolders.unshift(searchResultsFolder);
    }

    // Apply Consultant filtering to the updated folders
    const filteredFolders = updatedFolders;

    // Update the accordion data service
    this.accordionDataService.setFolders(filteredFolders);
  }
  // New methods for hierarchical folder display
  private updateDisplayFolders(foldersList: AccordionFolder[], userOwnerID: string): AccordionFolder[] {
    let folders;
    if (foldersList.length > 0) {
      folders = foldersList;
    } else {
      folders = this.accordionDataService.getCurrentFolders(userOwnerID);
    }

    if (foldersList.length > 0) {
      return this.syncExpansionStates(folders, userOwnerID);
    } else {
      return this.buildHierarchicalDisplay(folders);
    }
  }

  /**
   * Simplified sync of expansion states for hierarchical folders
   */
  private syncExpansionStates(folders: any[], userOwnerID?: string): any[] {
    const stateMap = new Map(this.accordionDataService.getCurrentFolders(userOwnerID).map((f: any) => [f.id, f]));

    const sync = (folder: any): any => ({
      ...folder,
      ...stateMap.get(folder.id),
      children: folder.children?.map(sync)
    });

    return folders.map(sync);
  }

  private buildHierarchicalDisplay(folders: AccordionFolder[]): AccordionFolder[] {
    const folderMap = new Map<string, AccordionFolder>();
    const rootFolders: AccordionFolder[] = [];

    // Create map and identify root folders
    folders.forEach(folder => {
      folderMap.set(folder.id, folder);
      if (!folder.parentId) {
        rootFolders.push(folder);
      }
    });

    // Build children arrays and set levels
    folders.forEach(folder => {
      if (folder.parentId) {
        const parent = folderMap.get(folder.parentId);
        if (parent) {
          if (!parent.children) parent.children = [];
          if (!parent.children.some(child => child.id === folder.id)) {
            parent.children.push(folder);
          }
        }
      }
    });

    // Set levels recursively
    const setLevels = (folderList: AccordionFolder[], level = 0) => {
      folderList.forEach(folder => {
        folder.level = level;
        if (folder.children?.length) {
          setLevels(folder.children, level + 1);
        }
      });
    };

    setLevels(rootFolders);
    return rootFolders;
  }

  getFolderIndent(folder: AccordionFolder): string {
    // Get the level from the folder's level property
    const level = folder.level || 0;
    return `${level * 20}px`;
  }

  hasChildren(folder: AccordionFolder): boolean {
    // Check if this folder has children by looking at the folder's children array
    if (folder.children && folder.children.length > 0) {
      return true;
    }
    // Fallback to searching in display folders
    return this.folders.some(f => f.parentId === folder.id);
    // return this.accordionFolders.some(f => f.parentId === folder.id);
    // return this.displayFolders.some(f => f.parentId === folder.id);
  }

  getChildren(folder: AccordionFolder): AccordionFolder[] {
    // Get children from the folder's children array
    if (folder.children && folder.children.length > 0) {
      return folder.children;
    }
    // Fallback to searching in display folders
    return this.accordionFolders.filter(f => f.parentId === folder.id);
    // return this.displayFolders.filter(f => f.parentId === folder.id);
  }

  toggleChildrenVisibility(folder: AccordionFolder): void {
    // This method is called when a folder with children is clicked
    // The expansion state is already handled in onFolderClick
    // This method can be used for additional child-specific logic if needed
  }

  isChildrenExpanded(folder: AccordionFolder): boolean {
    // For folders with children, check if any of their children are visible
    // This is different from the folder's own expansion state
    const children = this.getChildren(folder);
    return (
      children.length > 0 &&
      children.some(child => {
        // Check if the child is expanded in the display folders
        return child.expanded || false;
      })
    );
  }

  /**
   * Check if a folder is a predefined folder that should not be deleted
   * @param folder The folder to check
   * @returns true if the folder should not be deleted, false otherwise
   */
  isPredefinedFolder(folder: AccordionFolder): boolean {
    // INDEX folder (identified by name or id)
    if (folder.name === 'INDEX' || folder.id === 'all') {
      return true;
    }

    // Search results folder
    if (folder.type === 'search-results') {
      return true;
    }

    // Add other predefined folder checks here as needed
    // For example, if there are other system folders that shouldn't be deleted

    return false;
  }

  onDeleteFolder(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    // Emit delete folder event to parent component.
    // The parent is responsible for checking Firebase whether the folder is empty before proceeding.
    this.deleteFolder.emit({
      folder: folder,
      event: event
    });
  }

  onCreateSubfolder(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    // Emit create subfolder event to parent component
    this.createSubfolder.emit({
      folder: folder,
      event: event
    });
  }

  // Subfolder expansion methods
  isSubfolderExpanded(subfolder: AccordionFolder): boolean {
    return this.subfolderExpansionStates.get(subfolder.id) || false;
  }

  toggleSubfolderExpansion(subfolder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    const currentState = this.subfolderExpansionStates.get(subfolder.id) || false;
    this.subfolderExpansionStates.set(subfolder.id, !currentState);

    // If expanding and no files loaded yet, load from cache or backend
    if (!currentState && !subfolder.files && !subfolder.loading) {
      // First try to get files from cache
      const cachedFiles = this.accordionDataService.getCachedFiles(subfolder.id);

      if (cachedFiles) {
        // Use cached files
        this.accordionDataService.updateFolderFiles(subfolder.id, cachedFiles);
        // Update pagination state for cached files
        this.updatePaginationState(subfolder.id, cachedFiles.length);
      } else {
        // Load from backend if not in cache
        this.accordionDataService.setFolderLoading(subfolder.id, true);
        this.loadFolderContents.emit(subfolder.id);
      }
    }
  }

  onSubfolderClick(subfolder: AccordionFolder, event: Event): void {
    event.stopPropagation();
    // The actual expansion is handled by the expand button
    // This method can be used for additional subfolder click logic if needed
  }

  /**
   * Check if a folder can have subfolders (only root level folders can have subfolders)
   * @param folder The folder to check
   * @returns true if the folder can have subfolders, false otherwise
   */
  canHaveSubfolders(folder: AccordionFolder): boolean {
    // Only root level folders (level 0 or no level) can have subfolders
    return (folder.level || 0) === 0;
  }

  /**
   * Check if a folder is at the maximum allowed depth (prevents grandchild folders)
   * @param folder The folder to check
   * @returns true if the folder is at maximum depth, false otherwise
   */
  isAtMaxDepth(folder: AccordionFolder): boolean {
    // Maximum depth is 1 (root -> subfolder), so level 1 is the maximum
    return (folder.level || 0) >= 1;
  }

  checkOwnership(file: FolderFile): boolean {
    if (!file.ownerID) {
      return true;
    } else {
    }
    if (file.ownerID === this.ownerID) {
      return true;
    }
    return false;
  }
}
